import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, Navigate } from 'react-router-dom';
import { PlayerInfo } from '../../models/PlayerInfo';
import { PlayerStatsInterface } from '../../models/PlayerStats';
import { fetchItemsWithCache } from '../../utils/apiCache';
import { getAdminSession } from '../../utils/adminSession';
import { amendPlayerUrl, entriesUrl, CACHE_KEYS } from '../../utils/apiEndpoints';
import { MAIN_TEAM_OPTIONS, OTHER_MAIN_TEAM } from '../../constants/mainTeams';

const PageContainer = styled.div`
  max-width: 900px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: var(--gray-900);
  color: var(--gray-50);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.label`
  font-weight: 600;
`;

const RequiredMark = styled.span`
  color: #f97373;
  margin-left: 0.15rem;
`;

const Select = styled.select`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const Input = styled.input`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const HelperText = styled.span`
  font-size: 0.8rem;
  color: var(--gray);
`;

const SubmitButton = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;

  &:hover:enabled {
    background: var(--primary-light);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #12331a;
  color: #c9f7d6;
`;

const ErrorMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #3a1010;
  color: #ffd1d1;
`;

const ActionsRow = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SecondaryButton = styled.button`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const LinkButton = styled(Link)`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const ColumnsLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
  gap: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const StatsPanel = styled.div`
  border-radius: 10px;
  border: 1px solid #374151;
  padding: 1rem;
  background: #020617;
  font-size: 0.9rem;
`;

const StatsHeader = styled.h2`
  font-size: 1rem;
  margin-bottom: 0.75rem;
`;


const DiffTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  font-size: 0.9rem;

  th,
  td {
    border: 1px solid #374151;
    padding: 0.4rem 0.6rem;
    text-align: left;
  }

  th {
    background: #111827;
  }
`;

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';
type Step = 'edit' | 'confirm';

type EditablePlayerFields = Pick<
  PlayerInfo,
  'name' | 'position' | 'number' | 'mainTeam' | 'mainTeamLink' | 'shoots' | 'sponsor' | 'sponsorLink' | 'sponsorImg' | 'funfact'
>;

type EditableStatsFields = Pick<
  PlayerStatsInterface,
  | 'gamesPlayed'
  | 'goals'
  | 'assists'
  | 'points'
  | 'ppg'
  | 'pims'
  | 'wins'
  | 'losses'
  | 'so'
  | 'ga'
  | 'gaa'
  | 'saves'
  | 'shotsAgainst'
  | 'svPercent'
>;

interface FieldDiff {
  field: string;
  label: string;
  oldValue: string;
  newValue: string;
}

const toTitleCase = (value: string): string => {
  if (!value) return value;

  return value
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const formatValue = (value: unknown): string =>
  value === undefined || value === null || value === '' ? '—' : String(value);

const AmendPlayer: React.FC = () => {
  const [players, setPlayers] = useState<PlayerInfo[]>([]);
  const [stats, setStats] = useState<PlayerStatsInterface[]>([]);
  const [goalieStats, setGoalieStats] = useState<PlayerStatsInterface[]>([]);
  const [selectedName, setSelectedName] = useState('');

  const [form, setForm] = useState<EditablePlayerFields>({
    name: '',
    position: '',
    number: 0,
    mainTeam: undefined,
    mainTeamLink: undefined,
    shoots: undefined,
    sponsor: undefined,
    sponsorLink: undefined,
    sponsorImg: undefined,
    funfact: undefined,
  });
  const [mainTeamSelection, setMainTeamSelection] = useState('');
  const [otherMainTeamName, setOtherMainTeamName] = useState('');

  const [original, setOriginal] = useState<EditablePlayerFields | null>(null);
  const [statsForm, setStatsForm] = useState<EditableStatsFields | null>(null);
  const [originalStats, setOriginalStats] = useState<EditableStatsFields | null>(null);
  const [step, setStep] = useState<Step>('edit');
  const [diffs, setDiffs] = useState<FieldDiff[]>([]);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imageFileName, setImageFileName] = useState<string | null>(null);
  const [isReadingImage, setIsReadingImage] = useState(false);
  const [playerImageError, setPlayerImageError] = useState<string | null>(null);

  const session = getAdminSession();
  const apiKey = session?.apiKey ?? null;
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (!apiKey) return;
        setLoading(true);
        const CACHE_TTL_MS = 5 * 60 * 1000;

        const [playersItems, statsItems, goalieStatsItems] = await Promise.all([
          fetchItemsWithCache<PlayerInfo>(
            entriesUrl('nomads_players'),
            CACHE_KEYS.players,
            CACHE_TTL_MS
          ),
          fetchItemsWithCache<PlayerStatsInterface>(
            entriesUrl('nomads_stats'),
            CACHE_KEYS.stats,
            CACHE_TTL_MS
          ),
          fetchItemsWithCache<PlayerStatsInterface>(
            entriesUrl('nomads_goalie_stats'),
            CACHE_KEYS.goalieStats,
            CACHE_TTL_MS
          ),
        ]);

        const sortedPlayers = [...playersItems].sort((a, b) => a.name.localeCompare(b.name));

        setPlayers(sortedPlayers);
        setStats(statsItems);
        setGoalieStats(goalieStatsItems);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Unable to load player data. Please try again.';
        setErrorMessage(message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [reloadKey, apiKey]);

  const selectedStats = useMemo(() => {
    if (!selectedName) return null;
    const playerInfo = players.find((p) => p.name === selectedName);
    const isGoalie =
      playerInfo?.position.split('/').some((pos) => pos.trim() === 'Goalie') ?? false;
    if (isGoalie) {
      return goalieStats.find((s) => s.player === selectedName) ?? null;
    }
    return stats.find((s) => s.player === selectedName) ?? null;
  }, [stats, goalieStats, selectedName, players]);

  const selectedPlayerIsGoalie = useMemo(() => {
    const playerInfo = players.find((p) => p.name === selectedName);
    return playerInfo?.position.split('/').some((pos) => pos.trim() === 'Goalie') ?? false;
  }, [players, selectedName]);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  const handleSelectPlayer = (name: string) => {
    setSelectedName(name);
    setStep('edit');
    setDiffs([]);
    setSubmitState('idle');
    setErrorMessage(null);
    setPlayerImageError(null);

    const info = players.find((p) => p.name === name);
    if (!info) return;

    const base: EditablePlayerFields = {
      name: info.name,
      position: info.position,
      number: info.number,
      mainTeam: info.mainTeam,
      mainTeamLink: info.mainTeamLink,
      shoots: info.shoots,
      sponsor: info.sponsor,
      sponsorLink: info.sponsorLink,
      sponsorImg: info.sponsorImg,
      funfact: info.funfact,
    };

    const isGoalie =
      info.position.split('/').some((pos) => pos.trim() === 'Goalie') ?? false;
    const stat = isGoalie
      ? goalieStats.find((s) => s.player === name)
      : stats.find((s) => s.player === name);

    const gamesPlayed = Number(stat?.gamesPlayed ?? 0) || 0;
    const baseStats: EditableStatsFields = isGoalie
      ? (() => {
          const ga = Number(stat?.ga ?? 0) || 0;
          const gaaComputed = gamesPlayed > 0 ? ga / gamesPlayed : 0;
          const saves = Number(stat?.saves ?? 0) || 0;
          const shotsAgainst = Number(stat?.shotsAgainst ?? 0) || 0;
          const svRatio = saves + shotsAgainst > 0 ? saves / (saves + shotsAgainst) : 0;
          const svPercent = svRatio * 100;

          return {
            gamesPlayed,
            wins: Number(stat?.wins ?? 0) || 0,
            losses: Number(stat?.losses ?? 0) || 0,
            so: Number(stat?.so ?? 0) || 0,
            ga,
            gaa: Number(stat?.gaa ?? gaaComputed) || gaaComputed,
            saves,
            shotsAgainst,
            svPercent: Number(stat?.svPercent ?? svPercent) || svPercent,
          };
        })()
      : {
          gamesPlayed,
          goals: Number(stat?.goals ?? 0) || 0,
          assists: Number(stat?.assists ?? 0) || 0,
          points: Number(stat?.points ?? 0) || 0,
          ppg: Number(stat?.ppg ?? 0) || 0,
          pims: Number(stat?.pims ?? 0) || 0,
          // goalie fields not used for skaters
          wins: 0,
          losses: 0,
          so: 0,
          ga: 0,
          gaa: 0,
          saves: 0,
          shotsAgainst: 0,
          svPercent: 0,
        };

    setOriginal(base);
    setForm(base);
    const knownTeam = MAIN_TEAM_OPTIONS.some((team) => team.name === (info.mainTeam ?? ''));
    if (!info.mainTeam) {
      setMainTeamSelection('');
      setOtherMainTeamName('');
    } else if (knownTeam) {
      setMainTeamSelection(info.mainTeam);
      setOtherMainTeamName('');
    } else {
      setMainTeamSelection(OTHER_MAIN_TEAM);
      setOtherMainTeamName(info.mainTeam);
    }
    setOriginalStats(baseStats);
    setStatsForm(baseStats);
    setImageBase64(null);
    setImageFileName(null);
    setIsReadingImage(false);
  };

  const updateField = <K extends keyof EditablePlayerFields>(field: K, value: EditablePlayerFields[K]) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateStatsField = <K extends keyof EditableStatsFields>(
    field: K,
    value: EditableStatsFields[K]
  ) => {
    setStatsForm((prev) => {
      const base: EditableStatsFields =
        prev ?? {
          gamesPlayed: 0,
          goals: 0,
          assists: 0,
          points: 0,
          ppg: 0,
          pims: 0,
          wins: 0,
          losses: 0,
          so: 0,
          ga: 0,
          gaa: 0,
          saves: 0,
          shotsAgainst: 0,
          svPercent: 0,
        };

      const next: EditableStatsFields = {
        ...base,
        [field]: value,
      };

      const gamesPlayed = Number(next.gamesPlayed) || 0;
      const goals = Number(next.goals) || 0;
      const assists = Number(next.assists) || 0;
      const points = goals + assists;
      const ppg = gamesPlayed > 0 ? points / gamesPlayed : 0;

      const ga = Number(next.ga) || 0;
      const gaa = gamesPlayed > 0 ? ga / gamesPlayed : 0;
      const saves = Number(next.saves) || 0;
      const shotsAgainst = Number(next.shotsAgainst) || 0;
      const svRatio = saves + shotsAgainst > 0 ? saves / (saves + shotsAgainst) : 0;
      const svPercent = svRatio * 100;

      return {
        ...next,
        points,
        ppg,
        gaa,
        svPercent,
      };
    });
  };

  const handlePlayerImageFile = (file: File | null) => {
    setErrorMessage(null);
    setPlayerImageError(null);

    if (!file) {
      setImageBase64(null);
      setImageFileName(null);
      setIsReadingImage(false);
      return;
    }

    const isPng =
      file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');
    if (!isPng) {
      setPlayerImageError('Player image must be a PNG file.');
      return;
    }

    const maxBytes = 400 * 1024;
    if (file.size > maxBytes) {
      setPlayerImageError('Player image must be 400KB or smaller.');
      return;
    }

    const reader = new FileReader();
    setIsReadingImage(true);
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') return;

      // data:[mime];base64,XXXXX -> take only XXXXX
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      setImageBase64(base64);
      setImageFileName(file.name);
      setPlayerImageError(null);
      setIsReadingImage(false);
    };
    reader.onerror = () => {
      setPlayerImageError('Unable to read selected image file.');
      setIsReadingImage(false);
    };

    reader.readAsDataURL(file);
  };

  const buildDiffs = (
    oldPlayer: EditablePlayerFields,
    updatedPlayer: EditablePlayerFields,
    oldStats?: EditableStatsFields | null,
    updatedStats?: EditableStatsFields | null,
    isGoalie?: boolean
  ): FieldDiff[] => {
    const playerMap: { field: keyof EditablePlayerFields; label: string }[] = [
      { field: 'name', label: 'Name' },
      { field: 'position', label: 'Position' },
      { field: 'number', label: 'Number' },
      { field: 'mainTeam', label: 'Main Team' },
      { field: 'mainTeamLink', label: 'Main Team URL' },
      { field: 'shoots', label: 'Shoots' },
      { field: 'sponsor', label: 'Sponsor Name' },
      { field: 'sponsorLink', label: 'Sponsor URL' },
      { field: 'sponsorImg', label: 'Sponsor Image URL' },
      { field: 'funfact', label: 'Fun Fact' },
    ];

    const result: FieldDiff[] = [];
    for (const { field, label } of playerMap) {
      const oldValue = formatValue(oldPlayer[field]);
      const newValue = formatValue(updatedPlayer[field]);
      if (oldValue !== newValue) {
        result.push({ field, label, oldValue, newValue });
      }
    }

    if (oldStats && updatedStats) {
      const statsMap: { field: keyof EditableStatsFields; label: string }[] = isGoalie
        ? [
            { field: 'gamesPlayed', label: 'Games Played' },
            { field: 'wins', label: 'Wins' },
            { field: 'losses', label: 'losses' },
            { field: 'so', label: 'SO' },
            { field: 'ga', label: 'GA' },
            { field: 'gaa', label: 'GAA' },
            { field: 'saves', label: 'Saves' },
            { field: 'shotsAgainst', label: 'Shots Against' },
            { field: 'svPercent', label: 'SV%' },
          ]
        : [
            { field: 'gamesPlayed', label: 'Games Played' },
            { field: 'goals', label: 'Goals' },
            { field: 'assists', label: 'Assists' },
            { field: 'points', label: 'Points' },
            { field: 'ppg', label: 'PPG' },
            { field: 'pims', label: 'PIMs' },
          ];

      for (const { field, label } of statsMap) {
        const oldValue = formatValue(oldStats[field]);
        const newValue = formatValue(updatedStats[field]);
        if (oldValue !== newValue) {
          result.push({ field: `stats.${field}`, label, oldValue, newValue });
        }
      }
    }

    return result;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!original) return;
    if (isReadingImage) {
      setErrorMessage('Please wait for the image to finish loading.');
      return;
    }
    if (playerImageError) return;

    const normalizedPlayer: EditablePlayerFields = {
      ...form,
      name: toTitleCase(form.name),
      position: toTitleCase(form.position),
      mainTeam: (form.mainTeam ?? '').trim() || undefined,
      mainTeamLink: (form.mainTeamLink ?? '').trim() || undefined,
      // keep number and other fields as-is
    };

    const currentPlayer: EditablePlayerFields = {
      ...normalizedPlayer,
      number: Number(normalizedPlayer.number) || 0,
    };

    const isGoalie =
      original.position.split('/').some((pos) => pos.trim() === 'Goalie') ?? false;

    const baseStats: EditableStatsFields = statsForm ?? {
      gamesPlayed: 0,
      goals: 0,
      assists: 0,
      points: 0,
      ppg: 0,
      pims: 0,
      wins: 0,
      losses: 0,
      so: 0,
      ga: 0,
      gaa: 0,
      saves: 0,
      shotsAgainst: 0,
      svPercent: 0,
    };

    const currentStats: EditableStatsFields = isGoalie
      ? {
          gamesPlayed: Number(baseStats.gamesPlayed) || 0,
          wins: Number(baseStats.wins) || 0,
          losses: Number(baseStats.losses) || 0,
          so: Number(baseStats.so) || 0,
          ga: Number(baseStats.ga) || 0,
          gaa: Number(baseStats.gaa) || 0,
          saves: Number(baseStats.saves) || 0,
          shotsAgainst: Number(baseStats.shotsAgainst) || 0,
          svPercent: Number(baseStats.svPercent) || 0,
        }
      : {
          gamesPlayed: Number(baseStats.gamesPlayed) || 0,
          goals: Number(baseStats.goals) || 0,
          assists: Number(baseStats.assists) || 0,
          points: Number(baseStats.points) || 0,
          ppg: Number(baseStats.ppg) || 0,
          pims: Number(baseStats.pims) || 0,
          // goalie fields not used for skaters
          wins: 0,
          losses: 0,
          so: 0,
          ga: 0,
          gaa: 0,
          saves: 0,
          shotsAgainst: 0,
          svPercent: 0,
        };

    let pendingDiffs = buildDiffs(original, currentPlayer, originalStats, currentStats, isGoalie);

    if (imageBase64 !== null) {
      pendingDiffs = [
        ...pendingDiffs,
        {
          field: 'imageBase64',
          label: 'Player Image',
          oldValue: '—',
          newValue: imageFileName ? `PNG (${imageFileName})` : 'PNG (added)',
        },
      ];
    }
    if (pendingDiffs.length === 0) {
      setErrorMessage('No changes detected to update.');
      return;
    }

    setForm(currentPlayer);
    setStatsForm(currentStats);
    setDiffs(pendingDiffs);
    setStep('confirm');
  };

  const handleConfirm = async () => {
    if (!original) return;
    setSubmitState('submitting');
    setErrorMessage(null);

    try {
      const isGoalie =
        original.position.split('/').some((pos) => pos.trim() === 'Goalie') ?? false;

      const payload = {
        tableName: 'nomads_players',
        teamName: 'nomads',
        currentName: original.name, // existing name in DB
        name: form.name, // new name (after any edits)
        position: form.position,
        number: Number(form.number) || 0,
        mainTeam: form.mainTeam || undefined,
        mainTeamLink: form.mainTeamLink || undefined,
        shoots: form.shoots || undefined,
        sponsor: form.sponsor || undefined,
        sponsorLink: form.sponsorLink || undefined,
        sponsorImg: form.sponsorImg || undefined,
        funfact: form.funfact || undefined,
        ...(imageBase64 !== null ? { imageBase64 } : {}),
        ...(isGoalie
          ? {
              // goalie stats fields
              gamesPlayed: statsForm?.gamesPlayed ?? 0,
              wins: statsForm?.wins ?? 0,
              losses: statsForm?.losses ?? 0,
              so: statsForm?.so ?? 0,
              ga: statsForm?.ga ?? 0,
              gaa: statsForm?.gaa ?? 0,
              saves: statsForm?.saves ?? 0,
              shotsAgainst: statsForm?.shotsAgainst ?? 0,
              SVPercent: statsForm?.svPercent ?? 0,
            }
          : {
              // skater stats fields
              gamesPlayed: statsForm?.gamesPlayed ?? 0,
              goals: statsForm?.goals ?? 0,
              assists: statsForm?.assists ?? 0,
              points: statsForm?.points ?? 0,
              ppg: statsForm?.ppg ?? 0,
              pims: statsForm?.pims ?? 0,
            }),
      };

      const response = await fetch(
        amendPlayerUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': session.apiKey,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to update player');
      }

      // Clear related caches so subsequent views refetch
      if (typeof window !== 'undefined') {
        localStorage.removeItem(CACHE_KEYS.players);
        localStorage.removeItem(CACHE_KEYS.stats);
        localStorage.removeItem(CACHE_KEYS.goalieStats);
      }

      setSubmitState('success');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unknown error while updating player';
      setErrorMessage(message);
      setSubmitState('error');
    }
  };

  const resetAll = () => {
    setSelectedName('');
    setOriginal(null);
    setForm({
      name: '',
      position: '',
      number: 0,
      mainTeam: undefined,
      mainTeamLink: undefined,
      shoots: undefined,
      sponsor: undefined,
      sponsorLink: undefined,
      sponsorImg: undefined,
      funfact: undefined,
    });
    setMainTeamSelection('');
    setOtherMainTeamName('');
    setStatsForm(null);
    setOriginalStats(null);
    setImageBase64(null);
    setImageFileName(null);
    setPlayerImageError(null);
    setStep('edit');
    setDiffs([]);
    setSubmitState('idle');
    setErrorMessage(null);
    setLoading(true);
    setReloadKey((key) => key + 1);
  };

  if (loading) {
    return (
      <PageContainer>
        <Title>Amend Player</Title>
        <div>Loading player data…</div>
      </PageContainer>
    );
  }

  if (submitState === 'success') {
    return (
      <PageContainer>
        <Title>Player Updated</Title>
        <Message>The player details were updated successfully.</Message>
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetAll}>
            Amend another player
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  if (submitState === 'error') {
    return (
      <PageContainer>
        <Title>Player Not Updated</Title>
        <ErrorMessage>
          There was a problem updating the player.
          {errorMessage ? <div style={{ marginTop: '0.5rem' }}>{errorMessage}</div> : null}
        </ErrorMessage>
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetAll}>
            Try again
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Title>Amend Player</Title>
      {errorMessage && step === 'edit' && (
        <ErrorMessage style={{ marginBottom: '1rem' }}>{errorMessage}</ErrorMessage>
      )}
      <ColumnsLayout>
        <div>
          {step === 'edit' && (
            <Form onSubmit={handleSubmit}>
              <FieldGroup>
                <Label htmlFor="playerSelect">
                  Player<RequiredMark>*</RequiredMark>
                </Label>
                <Select
                  id="playerSelect"
                  value={selectedName}
                  onChange={(e) => handleSelectPlayer(e.target.value)}
                  required
                >
                  <option value="">Select a player</option>
                  {players.map((p) => (
                    <option key={p.name} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </FieldGroup>

              {selectedName && (
                <>
                  <FieldGroup>
                    <Label htmlFor="name">
                      Name<RequiredMark>*</RequiredMark>
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={form.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      required
                    />
                  </FieldGroup>

                  <FieldGroup>
                    <Label htmlFor="position">
                      Position<RequiredMark>*</RequiredMark>
                    </Label>
                    <Select
                      id="position"
                      value={form.position}
                      onChange={(e) => updateField('position', e.target.value as EditablePlayerFields['position'])}
                      required
                    >
                      <option value="">Select position</option>
                      <option value="Forward">Forward</option>
                      <option value="Defense">Defense</option>
                      <option value="Goalie">Goalie</option>
                    </Select>
                  </FieldGroup>

                  <FieldGroup>
                    <Label htmlFor="number">
                      Number<RequiredMark>*</RequiredMark>
                    </Label>
                    <Input
                      id="number"
                      type="number"
                      min="0"
                      value={form.number}
                      onChange={(e) => updateField('number', Number(e.target.value) || 0)}
                      required
                    />
                  </FieldGroup>

                  <FieldGroup>
                    <Label htmlFor="mainTeam">Main Team</Label>
                    <Select
                      id="mainTeam"
                      value={mainTeamSelection}
                      onChange={(e) => {
                        const selected = e.target.value;
                        setMainTeamSelection(selected);
                        if (!selected) {
                          setOtherMainTeamName('');
                          updateField('mainTeam', undefined);
                          updateField('mainTeamLink', undefined);
                          return;
                        }
                        if (selected === OTHER_MAIN_TEAM) {
                          updateField('mainTeam', otherMainTeamName || undefined);
                          return;
                        }
                        setOtherMainTeamName('');
                        updateField('mainTeam', selected);
                        updateField('mainTeamLink', undefined);
                      }}
                    >
                      <option value="">Not set</option>
                      {MAIN_TEAM_OPTIONS.map((team) => (
                        <option key={team.name} value={team.name}>
                          {team.name}
                        </option>
                      ))}
                    </Select>
                  </FieldGroup>

                  {mainTeamSelection === OTHER_MAIN_TEAM && (
                    <>
                      <FieldGroup>
                        <Label htmlFor="otherMainTeamName">Other Main Team Name</Label>
                        <Input
                          id="otherMainTeamName"
                          type="text"
                          value={otherMainTeamName}
                          onChange={(e) => {
                            const value = e.target.value;
                            setOtherMainTeamName(value);
                            updateField('mainTeam', value || undefined);
                          }}
                        />
                      </FieldGroup>
                      <FieldGroup>
                        <Label htmlFor="mainTeamUrl">Main Team URL</Label>
                        <Input
                          id="mainTeamUrl"
                          type="url"
                          value={form.mainTeamLink ?? ''}
                          onChange={(e) => updateField('mainTeamLink', e.target.value || undefined)}
                          placeholder="https://example.com"
                        />
                      </FieldGroup>
                    </>
                  )}

                  <FieldGroup>
                    <Label htmlFor="shoots">Shoots (L/R)</Label>
                    <Select
                      id="shoots"
                      value={form.shoots ?? ''}
                      onChange={(e) =>
                        updateField(
                          'shoots',
                          e.target.value ? (e.target.value === 'Left' ? 'Left' : 'Right') : undefined
                        )
                      }
                    >
                      <option value="">Not set</option>
                      <option value="Left">L</option>
                      <option value="Right">R</option>
                    </Select>
                  </FieldGroup>

                  <FieldGroup>
                    <Label htmlFor="sponsorName">Sponsor Name</Label>
                    <Input
                      id="sponsorName"
                      type="text"
                      value={form.sponsor ?? ''}
                      onChange={(e) => updateField('sponsor', e.target.value || undefined)}
                    />
                  </FieldGroup>

                  <FieldGroup>
                    <Label htmlFor="sponsorUrl">Sponsor URL</Label>
                    <Input
                      id="sponsorUrl"
                      type="url"
                      value={form.sponsorLink ?? ''}
                      onChange={(e) => updateField('sponsorLink', e.target.value || undefined)}
                      placeholder="https://example.com"
                    />
                  </FieldGroup>

                  <FieldGroup>
                    <Label htmlFor="sponsorImageUrl">Sponsor Image URL</Label>
                    <Input
                      id="sponsorImageUrl"
                      type="url"
                      value={form.sponsorImg ?? ''}
                      onChange={(e) => updateField('sponsorImg', e.target.value || undefined)}
                    />
                  </FieldGroup>

                  <FieldGroup>
                    <Label htmlFor="playerImage">Player Image (PNG, max 400KB)</Label>
                    <Input
                      id="playerImage"
                      type="file"
                      accept="image/png"
                      onChange={(e) => handlePlayerImageFile(e.target.files ? e.target.files[0] : null)}
                    />
                    {playerImageError ? (
                      <ErrorMessage style={{ marginTop: '0.75rem', marginBottom: '0.25rem' }}>
                        {playerImageError}
                      </ErrorMessage>
                    ) : null}
                    {imageFileName ? (
                      <HelperText>Selected: {imageFileName}</HelperText>
                    ) : null}
                    {isReadingImage ? (
                      <HelperText style={{ marginTop: '0.5rem' }}>Reading image…</HelperText>
                    ) : null}
                    {imageBase64 ? (
                      <ActionsRow style={{ marginTop: '0.5rem' }}>
                        <SecondaryButton
                          type="button"
                          onClick={() => {
                            setImageBase64(null);
                            setImageFileName(null);
                            setIsReadingImage(false);
                            setPlayerImageError(null);
                          }}
                        >
                          Remove image
                        </SecondaryButton>
                      </ActionsRow>
                    ) : null}
                  </FieldGroup>

                  <FieldGroup>
                    <Label htmlFor="funFact">Fun Fact</Label>
                    <Input
                      id="funFact"
                      type="text"
                      value={form.funfact ?? ''}
                      onChange={(e) => updateField('funfact', e.target.value || undefined)}
                    />
                  </FieldGroup>

                  <ActionsRow>
                    <LinkButton to="/admin">Back to admin</LinkButton>
                    <SubmitButton
                      type="submit"
                      disabled={!selectedName || isReadingImage || !!playerImageError}
                    >
                      Review changes
                    </SubmitButton>
                  </ActionsRow>
                  <HelperText>* You will be asked to confirm changes before they are saved.</HelperText>
                </>
              )}
            </Form>
          )}

          {step === 'confirm' && original && (
            <>
              <p>
                Please review the changes for <strong>{original.name}</strong> before confirming.
              </p>
              <DiffTable>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Old value</th>
                    <th>New value</th>
                  </tr>
                </thead>
                <tbody>
                  {diffs.map((d) => (
                    <tr key={d.field}>
                      <td>{d.label}</td>
                      <td>{d.oldValue}</td>
                      <td>{d.newValue}</td>
                    </tr>
                  ))}
                </tbody>
              </DiffTable>
              <ActionsRow>
                <SecondaryButton type="button" onClick={() => setStep('edit')}>
                  Back to edit
                </SecondaryButton>
                <SubmitButton type="button" onClick={handleConfirm}>
                  Confirm and update
                </SubmitButton>
                <LinkButton to="/admin">Back to admin</LinkButton>
              </ActionsRow>
            </>
          )}
        </div>

        {step === 'edit' && (
          <StatsPanel>
            <StatsHeader>Current Season Stats</StatsHeader>
            {!selectedStats && !statsForm && (
              <HelperText>Select a player to see stats.</HelperText>
            )}
            {(selectedStats || statsForm) && (
              <>
                {selectedPlayerIsGoalie ? (
                  <>
                    <FieldGroup>
                      <Label htmlFor="gamesPlayed">Games Played</Label>
                      <Input
                        id="gamesPlayed"
                        type="number"
                        min="0"
                        value={statsForm?.gamesPlayed ?? selectedStats?.gamesPlayed ?? 0}
                        onChange={(e) =>
                          updateStatsField('gamesPlayed', Number(e.target.value) || 0)
                        }
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <Label htmlFor="wins">Wins</Label>
                      <Input
                        id="wins"
                        type="number"
                        min="0"
                        value={statsForm?.wins ?? selectedStats?.wins ?? 0}
                        onChange={(e) => updateStatsField('wins', Number(e.target.value) || 0)}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <Label htmlFor="losses">losses</Label>
                      <Input
                        id="losses"
                        type="number"
                        min="0"
                        value={statsForm?.losses ?? selectedStats?.losses ?? 0}
                        onChange={(e) => updateStatsField('losses', Number(e.target.value) || 0)}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <Label htmlFor="so">SO</Label>
                      <Input
                        id="so"
                        type="number"
                        min="0"
                        value={statsForm?.so ?? selectedStats?.so ?? 0}
                        onChange={(e) => updateStatsField('so', Number(e.target.value) || 0)}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <Label htmlFor="ga">GA</Label>
                      <Input
                        id="ga"
                        type="number"
                        min="0"
                        value={statsForm?.ga ?? selectedStats?.ga ?? 0}
                        onChange={(e) => updateStatsField('ga', Number(e.target.value) || 0)}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <Label htmlFor="gaa">GAA (GA / GP)</Label>
                      <Input
                        id="gaa"
                        type="number"
                        step="0.01"
                        min="0"
                        value={(statsForm?.gaa ?? selectedStats?.gaa ?? 0).toString()}
                        readOnly
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <Label htmlFor="saves">Saves</Label>
                      <Input
                        id="saves"
                        type="number"
                        min="0"
                        value={statsForm?.saves ?? selectedStats?.saves ?? 0}
                        onChange={(e) =>
                          updateStatsField('saves', Number(e.target.value) || 0)
                        }
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <Label htmlFor="shotsAgainst">Shots Against</Label>
                      <Input
                        id="shotsAgainst"
                        type="number"
                        min="0"
                        value={statsForm?.shotsAgainst ?? selectedStats?.shotsAgainst ?? 0}
                        onChange={(e) =>
                          updateStatsField('shotsAgainst', Number(e.target.value) || 0)
                        }
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <Label htmlFor="svPercent">SV% (Saves / (Saves + Shots))</Label>
                      <Input
                        id="svPercent"
                        type="number"
                        step="0.01"
                        min="0"
                        value={(statsForm?.svPercent ?? selectedStats?.svPercent ?? 0).toString()}
                        readOnly
                      />
                    </FieldGroup>
                  </>
                ) : (
                  <>
                    <FieldGroup>
                      <Label htmlFor="gamesPlayed">Games Played</Label>
                      <Input
                        id="gamesPlayed"
                        type="number"
                        min="0"
                        value={statsForm?.gamesPlayed ?? selectedStats?.gamesPlayed ?? 0}
                        onChange={(e) =>
                          updateStatsField('gamesPlayed', Number(e.target.value) || 0)
                        }
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <Label htmlFor="goals">Goals</Label>
                      <Input
                        id="goals"
                        type="number"
                        min="0"
                        value={statsForm?.goals ?? selectedStats?.goals ?? 0}
                        onChange={(e) =>
                          updateStatsField('goals', Number(e.target.value) || 0)
                        }
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <Label htmlFor="assists">Assists</Label>
                      <Input
                        id="assists"
                        type="number"
                        min="0"
                        value={statsForm?.assists ?? selectedStats?.assists ?? 0}
                        onChange={(e) =>
                          updateStatsField('assists', Number(e.target.value) || 0)
                        }
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <Label htmlFor="points">Points (G + A)</Label>
                      <Input
                        id="points"
                        type="number"
                        min="0"
                        value={statsForm?.points ?? selectedStats?.points ?? 0}
                        readOnly
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <Label htmlFor="ppg">PPG (Points / GP)</Label>
                      <Input
                        id="ppg"
                        type="number"
                        step="0.01"
                        min="0"
                        value={(statsForm?.ppg ?? selectedStats?.ppg ?? 0).toString()}
                        readOnly
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <Label htmlFor="pims">PIMs</Label>
                      <Input
                        id="pims"
                        type="number"
                        min="0"
                        value={statsForm?.pims ?? selectedStats?.pims ?? 0}
                        onChange={(e) =>
                          updateStatsField('pims', Number(e.target.value) || 0)
                        }
                      />
                    </FieldGroup>
                  </>
                )}
              </>
            )}
          </StatsPanel>
        )}
      </ColumnsLayout>
    </PageContainer>
  );
};

export default AmendPlayer;

